import React, { useCallback, useContext, useMemo } from 'react'
import { buildAccommodationCancellationPolicies, formatDate } from '@luxuryescapes/lib-refunds'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import { Refundable } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import Clickable from 'components/Common/Clickable'
import HelpPane from 'components/Common/HelpPane'
import ModalContext from 'contexts/ModalContext'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

function getRefundDescription(headlineCancellationPolicyType: 'fullyRefundable' | 'partiallyRefundable' | 'nonRefundable'): string {
  switch (headlineCancellationPolicyType) {
    case 'fullyRefundable':
      return Refundable.Fully
    case 'partiallyRefundable':
      return Refundable.Partial
    case 'nonRefundable':
      return Refundable.Non
    default:
      assertUnreachable(headlineCancellationPolicyType)
  }
}

interface Props {
  propertyTimeZone: string,
  headlineCancellationPolicyType: 'fullyRefundable' | 'partiallyRefundable' | 'nonRefundable';
  headlineCancellationPolicyStart: Date | undefined;
  fullyRefundableUntil: string | undefined;
  cancellationPolicies: Array<App.PropertyRateCancellationPolicy>
  currency: string;
  shouldClarifyRefundIsHotelOnly: boolean;
}

function PropertyCancellationPolicy({
  propertyTimeZone,
  headlineCancellationPolicyType,
  headlineCancellationPolicyStart,
  fullyRefundableUntil,
  cancellationPolicies,
  currency,
  shouldClarifyRefundIsHotelOnly,
}: Props) {
  const showModal = useContext(ModalContext)

  const refundDescription = getRefundDescription(headlineCancellationPolicyType)
  const { description: policyDescriptions } = useMemo(() => buildAccommodationCancellationPolicies({
    fullRefundableUntil: fullyRefundableUntil,
    cancellationPolicies,
  }, {
    timezone: propertyTimeZone,
    currencyCode: currency,
    hideExcludesFlights: !shouldClarifyRefundIsHotelOnly,
  }), [
    fullyRefundableUntil,
    cancellationPolicies,
    propertyTimeZone,
    currency,
    shouldClarifyRefundIsHotelOnly,
  ])

  const openModal = useCallback(() => {
    showModal(<Modal
      title={refundDescription}
    >
      {policyDescriptions.map((line, i) => <BodyText variant="large" as="p" key={i}>{ line }</BodyText>)}
      <HelpPane />
    </Modal>)
  }, [
    showModal,
    refundDescription,
    policyDescriptions,
  ])

  return <Clickable onClick={openModal}>
    <BodyText variant="medium">
      {headlineCancellationPolicyType !== 'nonRefundable' && <>
        <BodyText variant="medium" weight="bold" as="span" wrap="no-wrap" colour="highlight-secondary">
          {refundDescription}
        </BodyText>
        {' '}
        {headlineCancellationPolicyStart && <BodyText variant="medium" as="span">
          until {formatDate.formattedShortDateByTimezone(headlineCancellationPolicyStart, propertyTimeZone)}
        </BodyText>}
        {shouldClarifyRefundIsHotelOnly && ' (hotel only)'}
      </>}
      {headlineCancellationPolicyType === 'nonRefundable' && refundDescription}
      {' '}
      <LineInfoCircleIcon size="XS" />
    </BodyText>
  </Clickable>
}

export default React.memo(PropertyCancellationPolicy)
